/**
 * Hazard Layer Helpers
 *
 * Utilities for creating Mapbox GL JS layers
 * for hazard zone polygon overlays.
 * Colors vary by danger level score within each type.
 */

// Base hue per type, with light→dark variants by level
const HAZARD_TYPE_PALETTE: Record<
  string,
  { light: string; medium: string; dark: string }
> = {
  flood: {
    light: '#93c5fd', // blue-300
    medium: '#3b82f6', // blue-500
    dark: '#1e3a8a', // blue-900
  },
  landslide: {
    light: '#d6b48a',
    medium: '#92400e',
    dark: '#451a03',
  },
  rockslide: {
    light: '#d1d5db', // gray-300
    medium: '#6b7280', // gray-500
    dark: '#1f2937', // gray-800
  },
  avalanche: {
    light: '#a5f3fc', // cyan-200
    medium: '#06b6d4', // cyan-500
    dark: '#164e63', // cyan-900
  },
}

const DEFAULT_PALETTE = {
  light: '#d8b4fe',
  medium: '#9333ea',
  dark: '#581c87',
}

export function isHazardLayer(layerId: string): boolean {
  return layerId.startsWith('hazards-')
}

export function getHazardTypeFromLayerId(layerId: string): string {
  return layerId
    .replace('hazards-', '')
    .replace('-outline', '')
    .replace('-hover', '')
}

/**
 * Get base color for a hazard type (used for
 * outlines, legend key, and fallback)
 */
export function getHazardTypeColor(typeName: string): string {
  const palette = HAZARD_TYPE_PALETTE[typeName] || DEFAULT_PALETTE
  return palette.medium
}

/**
 * Get all 3 colors for a type (for legend)
 */
export function getHazardTypePalette(typeName: string) {
  return HAZARD_TYPE_PALETTE[typeName] || DEFAULT_PALETTE
}

/**
 * Build a Mapbox data-driven fill-opacity expression
 * that varies by levelScore (1→12 maps to 20%→80%)
 */
export function getHazardFillOpacityExpression(baseOpacity: number): any {
  // Scale factor: user opacity slider (0-100) as multiplier
  const scale = baseOpacity / 100
  return [
    'interpolate',
    ['linear'],
    ['get', 'levelScore'],
    0,
    0.2 * scale,
    3,
    0.35 * scale,
    6,
    0.5 * scale,
    9,
    0.65 * scale,
    12,
    0.8 * scale,
  ]
}

/**
 * Get all unique legend items from hazard zones
 * Returns type name + color for the legend
 */
export function getHazardLegendItems(
  hazardZones: any[]
): Array<{ name: string; color: string }> {
  const seen = new Set<string>()
  const items: Array<{ name: string; color: string }> = []
  for (const z of hazardZones) {
    const key = z.type || z.name
    if (key && !seen.has(key)) {
      seen.add(key)
      items.push({
        name: key,
        color: getHazardTypeColor(key),
      })
    }
  }
  return items
}

export function createHazardsGeoJSON(hazardZones: any[]): any {
  return {
    type: 'FeatureCollection',
    features: hazardZones
      .filter((z) => z.geometry)
      .map((z) => ({
        type: 'Feature',
        id: typeof z.id === 'string' ? parseInt(z.id, 10) : z.id,
        properties: {
          id: z.id,
          name: z.name,
          type: z.type,
          typeId: z.typeId,
          intensity: z.intensity,
          probability: z.probability,
          levelScore: z.levelScore,
        },
        geometry: z.geometry,
      })),
  }
}

export function formatHazardTooltip(properties: any): string {
  const name = properties.name || ''
  const intensity = properties.intensity || ''
  const probability = properties.probability || ''
  const score = properties.levelScore ?? 0

  const typeLabels: Record<string, string> = {
    flood: 'Inondation',
    landslide: 'Glissement de terrain',
    rockslide: 'Éboulement',
    avalanche: 'Avalanche',
  }

  const iMap: Record<string, string> = {
    low: 'faible',
    medium: 'moyenne',
    high: 'forte',
    residual: 'résiduelle',
  }

  const pMap: Record<string, string> = {
    '0-30 years': '0–30 ans',
    '30-100 years': '30–100 ans',
    '100-300 years': '100–300 ans',
    '300-1000 years': '300+ ans',
  }

  const label = typeLabels[name] || name
  const iStr = iMap[intensity] || ''
  const pStr = pMap[probability] || ''

  let desc = ''
  if (iStr && pStr) {
    desc = `${iStr.charAt(0).toUpperCase() + iStr.slice(1)} intensité — prob. ${pStr}`
  } else if (score > 0) {
    const level =
      score <= 2
        ? 'Danger faible'
        : score <= 5
          ? 'Danger moyen'
          : 'Danger élevé'
    desc = level
  }

  const bg = score <= 2 ? '#dbeafe' : score <= 4 ? '#fef3c7' : '#fee2e2'
  const fg = score <= 2 ? '#1e40af' : score <= 4 ? '#92400e' : '#991b1b'

  return (
    '<div style="padding:4px 2px;font-size:12px;line-height:1.5;">' +
    '<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">' +
    `<strong>${label}</strong>` +
    `<span style="background:${bg};color:${fg};padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600;white-space:nowrap;">${score} / 12</span>` +
    '</div>' +
    (desc
      ? `<div style="color:#64748b;font-size:11px;margin-top:2px;">${desc}</div>`
      : '') +
    '</div>'
  )
}
